import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { ErreurApi } from '../lib/api';
import {
  Carte,
  Chargement,
  MessageErreur,
  Pagination,
  Pastille,
  EnteteEcran,
} from '../components/ui/Primitives';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import { BarreSites } from '../components/ui/BarreSites';
import { ModaleConfirmation } from '../components/contrats/ModaleConfirmation';
import { FicheContrat } from '../components/contrats/FicheContrat';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { useTaillePage } from '../lib/useTaillePage';
import { appelApi } from '../lib/api';
import { urlDocumentGenere } from '../lib/useDocuments';
import {
  jourContrat,
  mentionRupture,
  useContrats,
  useRafraichirContrats,
  type ContratLigne,
  type EtatContrat,
} from '../lib/useContrats';

/**
 * Écran « Contrats d'apprentissage » du pôle administratif (CDC §4.2, §4.6).
 *
 * Il répond à une seule question, et le plus vite possible : **où en sont les
 * contrats**. Les six compteurs du haut donnent la réponse d'un coup d'œil et
 * servent eux-mêmes de filtre ; la liste, elle, ne porte que ce qui sert à la
 * gestion quotidienne — qui, où, quelle formation, quelle période, quel état.
 *
 * L'état affiché vient de l'API (`domain/contrat.ts`), libellé compris : il
 * n'est ni recalculé ni renommé ici. C'est ce qui garantit qu'un contrat rompu ne peut plus s'afficher
 * « Actif » : la rupture prime, et elle prime au même endroit pour tout le
 * monde.
 */

/** Filtres proposés en compteur, dans l'ordre de lecture de l'écran. */
const VUES: { cle: 'TOUS' | EtatContrat; libelle: string; ton: string }[] = [
  { cle: 'TOUS', libelle: 'Tous les contrats', ton: 'navy' },
  { cle: 'A_VALIDER', libelle: 'À valider', ton: 'warm' },
  { cle: 'VALIDE', libelle: 'Validés', ton: 'blue' },
  { cle: 'ACTIF', libelle: 'Actifs', ton: 'mint' },
  { cle: 'TERMINE', libelle: 'Terminés', ton: 'navy' },
  { cle: 'ROMPU', libelle: 'Rompus', ton: 'red' },
];

const TONS_COMPTEUR: Record<string, string> = {
  navy: 'text-codialis-navy',
  blue: 'text-codialis-sky',
  mint: 'text-codialis-teal',
  warm: 'text-amber-600',
  red: 'text-red-600',
};

/** Période du contrat en une ligne, lisible même quand une date manque. */
function periode(contrat: ContratLigne): string {
  if (!contrat.dateDebut && !contrat.dateFin) return 'Dates non arrêtées';
  return `${jourContrat(contrat.dateDebut)} → ${jourContrat(contrat.dateFin)}`;
}

/**
 * Carte-compteur cliquable. Elle reprend la `stat-card` de la maquette : c'est
 * le même objet visuel, doublé d'un filtre — cliquer sur « Rompus » n'ouvre pas
 * un autre écran, il restreint celui-ci.
 */
function CarteVue({
  libelle,
  valeur,
  ton,
  active,
  onClick,
}: {
  libelle: string;
  valeur: number;
  ton: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`stat-card rounded-2xl bg-white p-3 text-left shadow-card transition hover:shadow-lg sm:p-4 ${
        active ? 'ring-2 ring-codialis-navy' : ''
      }`}
    >
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {libelle}
      </div>
      <div className={`font-display mt-1 text-2xl font-extrabold sm:text-3xl ${TONS_COMPTEUR[ton]}`}>
        {valeur}
      </div>
    </button>
  );
}

/** Badge d'état, plus la mention de rupture quand il y en a une. */
function EtatContratCellule({
  contrat,
  onVoirRupture,
}: {
  contrat: ContratLigne;
  onVoirRupture: () => void;
}): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Pastille texte={contrat.etatLibelle} ton={contrat.etatTon} />
        {/* En second plan, le détail de l'API : où en est le document, et
            quand le contrat commence. */}
        {contrat.etatDetail ? (
          <span className="text-xs text-codialis-gray">{contrat.etatDetail}</span>
        ) : null}
      </div>
      {contrat.rupture && (
        <button
          type="button"
          onClick={onVoirRupture}
          className="relative inline-flex items-center gap-1 text-left text-xs font-semibold text-red-700 hover:underline"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{mentionRupture(contrat.rupture.dateRupture)} · voir la rupture</span>
        </button>
      )}
    </div>
  );
}

export function AdmContratsApprentissagePage(): JSX.Element {
  const { site } = usePerimetre();
  const naviguer = useNavigate();
  const { data, isLoading, error } = useContrats(site);
  const rafraichir = useRafraichirContrats();
  const notifier = useToast();

  const [vue, setVue] = useState<'TOUS' | EtatContrat>('TOUS');
  const [recherche, setRecherche] = useState('');
  const [formation, setFormation] = useState('');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  // Valider ouvre le contrôle du CERFA ; refuser demande une confirmation.
  // Aucune des deux décisions ne part sur un simple clic.
  const [immersionARefuser, setImmersionARefuser] = useState<ContratLigne | null>(null);
  const [ficheOuverte, setFicheOuverte] = useState<string | null>(null);
  const [documentRuptureApercu, setDocumentRuptureApercu] = useState<string | null>(null);
  /** CERFA ouvert en lecture, sans quitter la liste ni ouvrir la saisie. */
  const [documentCerfaApercu, setDocumentCerfaApercu] = useState<string | null>(null);
  const [refusEnCours, setRefusEnCours] = useState(false);

  const contrats = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    setPage(1);
  }, [vue, recherche, formation, du, au, site, taillePage]);

  const compteurs = useMemo(() => {
    const total: Record<string, number> = { TOUS: contrats.length };
    for (const contrat of contrats) {
      total[contrat.etat] = (total[contrat.etat] ?? 0) + 1;
    }
    return total;
  }, [contrats]);

  const formations = useMemo(
    () =>
      Array.from(new Set(contrats.map((contrat) => contrat.formation).filter(Boolean))).sort() as string[],
    [contrats],
  );

  const filtrees = useMemo(() => {
    const termes = recherche.trim().toLowerCase();
    return contrats.filter((contrat) => {
      if (vue !== 'TOUS' && contrat.etat !== vue) return false;
      if (formation && contrat.formation !== formation) return false;
      if (termes) {
        const champs = [contrat.candidat, contrat.entreprise, contrat.formation ?? '']
          .join(' ')
          .toLowerCase();
        if (!champs.includes(termes)) return false;
      }
      // La période porte sur le contrat : un contrat chevauchant la fenêtre
      // demandée est retenu. Une ligne sans contrat n'a pas de période à
      // comparer — elle sort du filtre plutôt que d'être retenue par défaut.
      if (du || au) {
        if (!contrat.dateDebut || !contrat.dateFin) return false;
        const debut = contrat.dateDebut.slice(0, 10);
        const fin = contrat.dateFin.slice(0, 10);
        if (du && fin < du) return false;
        if (au && debut > au) return false;
      }
      return true;
    });
  }, [contrats, vue, formation, recherche, du, au]);

  const total = filtrees.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const pageCourante = Math.min(page, pages);
  const lignes = useMemo(
    () => filtrees.slice((pageCourante - 1) * taillePage, pageCourante * taillePage),
    [filtrees, pageCourante, taillePage],
  );

  const fiche = ficheOuverte
    ? contrats.find((contrat) => contrat.id === ficheOuverte) ?? null
    : null;
  const filtresActifs = Boolean(recherche.trim() || formation || du || au || vue !== 'TOUS');

  const effacerFiltres = (): void => {
    setVue('TOUS');
    setRecherche('');
    setFormation('');
    setDu('');
    setAu('');
  };

  const confirmerRefus = async (): Promise<void> => {
    if (!immersionARefuser) return;
    setRefusEnCours(true);
    try {
      await appelApi(`/contrats/${immersionARefuser.id}/refuser`, { methode: 'POST' });
      notifier(`Contrat refusé pour ${immersionARefuser.candidat}.`);
      setImmersionARefuser(null);
      void rafraichir();
    } catch (erreur) {
      notifier(
        erreur instanceof ErreurApi ? erreur.message : 'Le refus n’a pas pu être enregistré.',
      );
    } finally {
      setRefusEnCours(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Contrats d'apprentissage"
        sousTitre="Pôle administratif · validation du CERFA, suivi des contrats et des ruptures"
      />
      <BarreSites />

      {isLoading ? (
        <Chargement libelle="Chargement des contrats…" />
      ) : error ? (
        <MessageErreur message="Les contrats n’ont pas pu être chargés. Réessayer dans un instant." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {VUES.map((entree) => (
              <CarteVue
                key={entree.cle}
                libelle={entree.libelle}
                valeur={compteurs[entree.cle] ?? 0}
                ton={entree.ton}
                active={vue === entree.cle}
                onClick={() => setVue(vue === entree.cle ? 'TOUS' : entree.cle)}
              />
            ))}
          </div>

          <Carte
            titre={
              vue === 'TOUS'
                ? 'Tous les contrats'
                : (VUES.find((entree) => entree.cle === vue)?.libelle ?? 'Refusés')
            }
            sansBordure
            actions={
              filtresActifs ? (
                <button
                  type="button"
                  onClick={effacerFiltres}
                  className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline"
                >
                  Effacer les filtres
                </button>
              ) : null
            }
          >
            <div className="flex flex-wrap items-end gap-2 border-b border-gray-100 p-3 sm:p-4">
              <ChampRecherche
                valeur={recherche}
                onChange={setRecherche}
                etiquette="Rechercher un contrat"
                placeholder="Apprenti, entreprise, formation…"
              />
              <ChampFiltre valeur={vue} onChange={(valeur) => setVue(valeur as 'TOUS' | EtatContrat)} etiquette="État du contrat">
                <option value="TOUS">Tous les états</option>
                <option value="A_VALIDER">À valider</option>
                <option value="VALIDE">Validés</option>
                <option value="ACTIF">Actifs</option>
                <option value="TERMINE">Terminés</option>
                <option value="ROMPU">Rompus</option>
                <option value="REFUSE">Refusés</option>
              </ChampFiltre>
              <ChampFiltre valeur={formation} onChange={setFormation} etiquette="Formation">
                <option value="">Toutes les formations</option>
                {formations.map((titre) => (
                  <option key={titre} value={titre}>
                    {titre}
                  </option>
                ))}
              </ChampFiltre>
              <label className="flex min-w-0 items-center gap-2 text-xs text-codialis-gray">
                <span className="shrink-0">Du</span>
                <input
                  type="date"
                  value={du}
                  onChange={(evenement) => setDu(evenement.target.value)}
                  className="min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-xs sm:w-auto"
                />
              </label>
              <label className="flex min-w-0 items-center gap-2 text-xs text-codialis-gray">
                <span className="shrink-0">au</span>
                <input
                  type="date"
                  value={au}
                  onChange={(evenement) => setAu(evenement.target.value)}
                  className="min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-xs sm:w-auto"
                />
              </label>
            </div>

            {lignes.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-codialis-gray">
                {contrats.length === 0
                  ? 'Aucun contrat d’apprentissage dans le périmètre affiché.'
                  : 'Aucun contrat ne correspond à la recherche et aux filtres.'}
              </div>
            ) : (
              <>
                {/* Tableau à partir de la tablette ; en dessous, une carte par
                    contrat — un tableau de six colonnes ne se lit pas à 375 px,
                    et le faire défiler à l'horizontale se paie à chaque ligne. */}
                <div className="hidden md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold">Apprenti(e)</th>
                        <th className="px-4 py-3 text-left font-bold">Entreprise</th>
                        <th className="px-4 py-3 text-left font-bold">Formation</th>
                        <th className="px-4 py-3 text-left font-bold">Contrat</th>
                        <th className="px-4 py-3 text-left font-bold">État</th>
                        <th className="px-4 py-3 text-right font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {lignes.map((contrat) => (
                        <tr key={contrat.id} className="align-top transition hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => setFicheOuverte(contrat.id)}
                              className="relative text-left font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
                            >
                              {contrat.candidat}
                            </button>
                            <div className="text-[11px] text-codialis-gray">{contrat.siteCode}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/ped-repertoire/${contrat.entrepriseId}`}
                              className="text-codialis-sky hover:underline"
                            >
                              {contrat.entreprise}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-codialis-gray">
                            {contrat.formation ?? 'Non assignée'}
                          </td>
                          <td className="px-4 py-3 text-xs text-codialis-gray">
                            {periode(contrat)}
                          </td>
                          <td className="px-4 py-3">
                            <EtatContratCellule
                              contrat={contrat}
                              onVoirRupture={() => setFicheOuverte(contrat.id)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap justify-end gap-2">
                              {contrat.etat === 'A_VALIDER' ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => naviguer(`/adm-contrats-apprentissage/${contrat.id}`)}
                                    className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg bg-codialis-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-codialis-deep"
                                  >
                                    Valider
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setImmersionARefuser(contrat)}
                                    className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                                  >
                                    Refuser
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setFicheOuverte(contrat.id)}
                                  className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                                >
                                  Ouvrir le contrat
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="divide-y divide-gray-100 md:hidden">
                  {lignes.map((contrat) => (
                    <li key={contrat.id} className="space-y-2 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => setFicheOuverte(contrat.id)}
                            className="relative text-left font-semibold text-codialis-navy"
                          >
                            {contrat.candidat}
                          </button>
                          <div className="truncate text-xs text-codialis-gray">
                            {contrat.entreprise}
                          </div>
                        </div>
                        <Pastille texte={contrat.etatLibelle} ton={contrat.etatTon} />
                      </div>
                      <div className="text-xs text-codialis-gray">
                        {contrat.formation ?? 'Formation non assignée'}
                      </div>
                      <div className="text-xs text-codialis-gray">{periode(contrat)}</div>
                      {contrat.etatDetail ? (
                        <div className="text-xs text-codialis-gray">{contrat.etatDetail}</div>
                      ) : null}
                      {contrat.rupture && (
                        <div className="flex items-center gap-1 text-xs font-semibold text-red-700">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {mentionRupture(contrat.rupture.dateRupture)}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {contrat.etat === 'A_VALIDER' ? (
                          <>
                            <button
                              type="button"
                              onClick={() => naviguer(`/adm-contrats-apprentissage/${contrat.id}`)}
                              className="min-h-[40px] flex-1 rounded-lg bg-codialis-navy px-3 py-2 text-xs font-semibold text-white"
                            >
                              Valider
                            </button>
                            <button
                              type="button"
                              onClick={() => setImmersionARefuser(contrat)}
                              className="min-h-[40px] flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-codialis-navy"
                            >
                              Refuser
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setFicheOuverte(contrat.id)}
                            className="min-h-[40px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-codialis-navy"
                          >
                            Ouvrir le contrat
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                <Pagination
                  page={pageCourante}
                  pages={pages}
                  total={total}
                  affiches={lignes.length}
                  taillePage={taillePage}
                  onPage={setPage}
                  onChangerTaille={setTaillePage}
                  uniteLabel="contrat"
                />
              </>
            )}
          </Carte>
        </>
      )}

      <FicheContrat
        contrat={fiche}
        onFermer={() => setFicheOuverte(null)}
        onOuvrirCerfa={(immersionId) => {
          setFicheOuverte(null);
          naviguer(`/adm-contrats-apprentissage/${immersionId}`);
        }}
        onOuvrirDocumentCerfa={setDocumentCerfaApercu}
        onOuvrirDocumentRupture={setDocumentRuptureApercu}
      />

      {documentCerfaApercu && (
        <VisionneusePiece
          ouverte
          libellePiece="CERFA d’apprentissage"
          nomFichier="cerfa-apprentissage.pdf"
          urlBase={urlDocumentGenere(documentCerfaApercu)}
          onFermer={() => setDocumentCerfaApercu(null)}
        />
      )}

      {documentRuptureApercu && (
        <VisionneusePiece
          ouverte
          libellePiece="Document de rupture"
          nomFichier="rupture-contrat.pdf"
          urlBase={urlDocumentGenere(documentRuptureApercu)}
          onFermer={() => setDocumentRuptureApercu(null)}
        />
      )}

      <ModaleConfirmation
        ouverte={immersionARefuser !== null}
        titre="Refuser le contrat"
        message={
          immersionARefuser
            ? `Le contrat de ${immersionARefuser.candidat} chez ${immersionARefuser.entreprise} sera marqué comme refusé.`
            : ''
        }
        libelleConfirmer="Refuser le contrat"
        enCours={refusEnCours}
        onConfirmer={confirmerRefus}
        onAnnuler={() => setImmersionARefuser(null)}
      />
    </div>
  );
}
